import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Icon,
  HStack,
  useToast,
} from "@chakra-ui/react";
import { IoLanguage, IoCheckmark } from "react-icons/io5";
import { supportedLocales, defaultLocale, SupportedLocale } from "../../i18n";

export const LanguageSwitcher: React.FC = () => {
  const { i18n, t } = useTranslation("common");
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const currentLocale = i18n.language as SupportedLocale;

  const switchLanguage = (newLocale: SupportedLocale) => {
    if (newLocale === currentLocale) return;

    // Get current path without locale prefix
    let currentPath = location.pathname;

    // Remove current locale prefix if present
    if (currentLocale !== defaultLocale && currentPath.startsWith(`/${currentLocale}`)) {
      currentPath = currentPath.slice(currentLocale.length + 1) || "/";
    }

    // Add new locale prefix (or none for default)
    const newPath = newLocale === defaultLocale ? currentPath : `/${newLocale}${currentPath}`;

    i18n.changeLanguage(newLocale);
    navigate(newPath + location.search, { replace: true });

    // Show toast in the new language
    const newT = i18n.getFixedT(newLocale, "common");
    toast({
      title: newT("language.switched"),
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  // Format language display: "Translated (Native)" or just "Native" if same
  const formatLanguageName = (locale: SupportedLocale) => {
    const translated = t(`language.${locale}`);
    const native = t(`language.native.${locale}`);
    return translated === native ? native : `${translated} (${native})`;
  };

  return (
    <Menu>
      <MenuButton
        as={Button}
        variant="ghost"
        size="md"
        leftIcon={<Icon as={IoLanguage} boxSize={5} />}
        aria-label={t("language.switchLanguage")}
        fontSize="md"
        data-testid="language-switcher"
      >
        {t("language.translate")}
      </MenuButton>
      <MenuList>
        {supportedLocales.map((locale) => {
          const isSelected = locale === currentLocale;
          return (
            <MenuItem
              key={locale}
              onClick={() => switchLanguage(locale)}
              aria-current={isSelected ? "true" : undefined}
              lang={locale}
              color={isSelected ? "blue.600" : undefined}
              fontWeight={isSelected ? "semibold" : "normal"}
              fontSize="md"
            >
              <HStack spacing={2} width="full">
                <Icon
                  as={IoCheckmark}
                  boxSize={5}
                  visibility={isSelected ? "visible" : "hidden"}
                  aria-hidden="true"
                />
                <span>{formatLanguageName(locale)}</span>
              </HStack>
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  );
};

export default LanguageSwitcher;
